// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  LIFECYCLE_CHARGE_LAG_HEX,
  LIFECYCLE_DATE_LAG_HEX,
  LIFECYCLE_INVOICE_REVIEW_HEX,
} from '@/billing/constants/lifecycle-segment-colors';

// The seven billing-lifecycle segment keys. The first six (chargeLag →
// delinquency) render as Gantt bars; `totalCycle` is the aggregate sum.
export type SegmentKey =
  | 'chargeLag'
  | 'invoiceReview'
  | 'deliveryLag'
  | 'dateLag'
  | 'actualTerms'
  | 'delinquency'
  | 'totalCycle';

// Display metadata for a single lifecycle segment — label, tooltip copy,
// and the two color tokens (one for typography, one for bar fills).
export interface SegmentDefinition {
  key: SegmentKey;
  label: string;
  tooltip: string;
  color: string;
  barColor: string;
}

// Single source of truth for segment labels, tooltip copy, and colors.
// Consumed by summary cards, Gantt bars, the table cells, and the detail
// tooltip breakdown. If a segment's color or copy changes, it changes here.
export const SEGMENTS: SegmentDefinition[] = [
  {
    key: 'chargeLag',
    label: 'Charge lag',
    tooltip:
      'How long charges are eligible to bill before they are moved onto a draft invoice. Includes only charges with an eligible billing date and invoices that have been drafted. (Can be negative if a charge is manually made billable before it was eligible to bill.)',
    color: LIFECYCLE_CHARGE_LAG_HEX,
    barColor: LIFECYCLE_CHARGE_LAG_HEX,
  },
  {
    key: 'invoiceReview',
    label: 'Invoice review',
    tooltip:
      'How long invoices sit in Draft status for review and edit before moving to Open status. Includes only invoices that have been drafted and opened. (Cannot be negative.)',
    color: LIFECYCLE_INVOICE_REVIEW_HEX,
    barColor: LIFECYCLE_INVOICE_REVIEW_HEX,
  },
  {
    key: 'deliveryLag',
    label: 'Delivery lag',
    tooltip:
      'How long after an invoice is opened before it is sent to the customer. Includes only invoices that have been opened and sent. (Can be negative if an invoice is drafted and then reported as sent in the past.)',
    color: 'success.main',
    barColor: 'success.main',
  },
  {
    key: 'dateLag',
    label: 'Date lag',
    tooltip:
      'How long between the invoice date printed on the invoice and the time the invoice was actually sent. Includes only invoices that have been sent. (Can be negative if the invoice date was set to a date before the invoice was sent.)',
    color: LIFECYCLE_DATE_LAG_HEX,
    barColor: LIFECYCLE_DATE_LAG_HEX,
  },
  {
    key: 'actualTerms',
    label: 'Actual terms',
    tooltip:
      'How long between the invoice date printed on the invoice and the due date printed on the invoice. Includes only invoices that are open and have a due date. (Can be negative if an invoice has a due date specified before the invoice date.)',
    color: 'text.secondary',
    barColor: 'text.secondary',
  },
  {
    key: 'delinquency',
    label: 'Delinquency',
    tooltip:
      'How long between the due date printed on the invoice and when the invoice is fully paid. Includes only invoices that have a due date and have been paid. (Can be negative if paid or credited early.)',
    color: 'error.main',
    barColor: 'error.main',
  },
  {
    key: 'totalCycle',
    label: 'Total cycle',
    tooltip:
      'Full end-to-end time from when a charge becomes billing-eligible until payment is received. Includes only paid invoices with a billing eligible date. Equal to the sum of the six segments above.',
    color: 'primary.main',
    barColor: 'primary.main',
  },
];

// The six segment keys that render as Gantt bars. `totalCycle` is the
// aggregate sum (the full chain span) and does not get its own row.
export const GANTT_SEGMENT_KEYS: SegmentKey[] = [
  'chargeLag',
  'invoiceReview',
  'deliveryLag',
  'dateLag',
  'actualTerms',
  'delinquency',
];

// Cluster definitions for grouping cards under ADS / ADC braces. Date lag
// sits at the top of ADC because it reconciles the ADS "send" anchor with
// the ADC "invoice date" anchor — without it the ADC chain wouldn't start
// at the same calendar point as ADS ends.
export const ADS_ADC_CLUSTERS = [
  {
    id: 'ads',
    label: 'ADS',
    fullLabel: 'Average days to send',
    description:
      'The total internal lag from when a charge becomes billing-eligible to when the invoice is sent to the customer. Composed of charge lag + invoice review + delivery lag. Lower is better — this measures the part of the collection cycle that your team controls.',
    segmentKeys: ['chargeLag', 'invoiceReview', 'deliveryLag'] as SegmentKey[],
  },
  {
    id: 'adc',
    label: 'ADC',
    fullLabel: 'Average days to collect',
    description:
      'The time from the invoice date to payment. Composed of date lag (the gap between send date and printed invoice date) + actual terms (the printed payment window) + delinquency (days past the printed due date). This measures the part of the collection cycle that the customer controls, with date lag reconciling the send anchor to the invoice-date anchor.',
    segmentKeys: ['dateLag', 'actualTerms', 'delinquency'] as SegmentKey[],
  },
];
